import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './OAuthTestResultPage.module.css';

import { tr } from '@/i18n';
const CheckCircleIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="32" fill="url(#successGrad)" />
    <path
      d="M20 32L28 40L44 24"
      stroke="white"
      strokeWidth="3.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient id="successGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
        <stop stopColor="#22c55e" />
        <stop offset="1" stopColor="#16a34a" />
      </linearGradient>
    </defs>
  </svg>
);

const ErrorCircleIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="32" fill="url(#errorGrad)" />
    <path
      d="M22 22L42 42M42 22L22 42"
      stroke="white"
      strokeWidth="3.5"
      strokeLinecap="round"
    />
    <defs>
      <linearGradient id="errorGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
        <stop stopColor="#ef4444" />
        <stop offset="1" stopColor="#dc2626" />
      </linearGradient>
    </defs>
  </svg>
);

export const OAuthTestResultPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(8);

  const status = searchParams.get('status');
  const providerName = searchParams.get('provider') ?? tr("ui.oauth_provider", "OAuth Provider");
  const email = searchParams.get('email') ?? '';
  const errorMessage = searchParams.get('message') ?? tr("ui.an_unknown_error_occurred_during_authentication", "An unknown error occurred during authentication.");

  const isSuccess = status === 'success';

  useEffect(() => {
    if (!isSuccess) return;
    if (countdown <= 0) {
      window.close();
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, isSuccess]);

  const handleClose = () => {
    window.close();
  };

  return (
    <div className={styles.page}>
      <div className={styles.backgroundOrbs}>
        <div className={`${styles.orb} ${styles.orb1} ${isSuccess ? styles.orbSuccess : styles.orbError}`} />
        <div className={`${styles.orb} ${styles.orb2} ${isSuccess ? styles.orbSuccess2 : styles.orbError2}`} />
        <div className={styles.orb3} />
      </div>

      <div className={styles.card}>
        <div className={`${styles.iconWrapper} ${isSuccess ? styles.iconSuccess : styles.iconError}`}>
          {isSuccess ? <CheckCircleIcon /> : <ErrorCircleIcon />}
        </div>

        {isSuccess ? (
          <>
            <h1 className={styles.title}>{tr("ui.connection_successful", "Connection Successful!")}</h1>
            <p className={styles.subtitle}>
              <strong>{providerName}</strong> {tr("ui.was_authenticated_successfully", "was authenticated successfully.")}
            </p>

            {email && (
              <div className={styles.detailCard}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{tr("ui.authenticated_as", "Authenticated as")}</span>
                  <span className={styles.detailValue}>{email}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{tr("ui.provider", "Provider")}</span>
                  <span className={styles.detailValue}>{providerName}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>{tr("common.status", "Status")}</span>
                  <span className={`${styles.detailValue} ${styles.statusBadge}`}>
                    <span className={styles.statusDot} />
                    {tr("common.active", "Active")}
                  </span>
                </div>
              </div>
            )}

            <p className={styles.countdownText}>
              {tr("ui.this_tab_will_close_automatically_in", "This tab will close automatically in")} <strong>{tr("task_monitor.duration_s", "{n}s", { n: countdown })}</strong>
            </p>
          </>
        ) : (
          <>
            <h1 className={`${styles.title} ${styles.titleError}`}>{tr("ui.connection_failed", "Connection Failed")}</h1>
            <p className={styles.subtitle}>
              {tr("ui.the_test_connection_to", "The test connection to")} <strong>{providerName}</strong> {tr("ui.encountered_an_error", "encountered an error.")}
            </p>

            <div className={`${styles.detailCard} ${styles.errorCard}`}>
              <div className={styles.errorLabel}>{tr("ui.error_details", "Error Details")}</div>
              <p className={styles.errorMessage}>{decodeURIComponent(errorMessage)}</p>
            </div>

            <p className={styles.errorHint}>
              {tr("ui.please_check_your_oauth_provider_configuration_a_79f4dc", "Please check your OAuth provider configuration and try again.")}
            </p>
          </>
        )}

        <button className={`${styles.closeBtn} ${isSuccess ? styles.closeBtnSuccess : styles.closeBtnError}`} onClick={handleClose}>
          {tr("ui.close_tab", "Close Tab")}
        </button>
      </div>
    </div>
  );
};
